'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import { useSession, signIn } from 'next-auth/react';
import { useLang, T } from '@/lib/i18n';
import styles from '../styles/Home.module.css';

function Tabs(props: React.PropsWithChildren<{}>) {
  const searchParams = useSearchParams();
  const tabIndex = searchParams?.get('tab') === 'custom' ? 1 : 0;

  const router = useRouter();
  function onTabSelected(index: number) {
    const tab = index === 1 ? 'custom' : 'demo';
    router.push(`/?tab=${tab}`);
  }

  let tabs = React.Children.map(props.children, (child, index) => {
    return (
      <button
        className="lk-button"
        onClick={() => {
          if (onTabSelected) {
            onTabSelected(index);
          }
        }}
        aria-pressed={tabIndex === index}
      >
        {/* @ts-ignore */}
        {child?.props.label}
      </button>
    );
  });

  return (
    <div className={styles.tabContainer}>
      <div className={styles.tabSelect}>{tabs}</div>
      {/* @ts-ignore */}
      {props.children[tabIndex]}
    </div>
  );
}

function DemoMeetingTab(props: { label: string }) {
  const router = useRouter();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  const [roomName, setRoomName] = useState('');
  const { data: session } = useSession();
  const lang: 'ru' | 'en' = useLang();
  const startMeeting = () => {
    const rid = roomName.trim() ? roomName.trim().replace(/\s+/g, '-').toLowerCase() : generateRoomId();
    if (e2ee) {
      router.push(`/rooms/${rid}#${encodePassphrase(sharedPassphrase)}`);
    } else {
      router.push(`/rooms/${rid}`);
    }
  };
  return (
    <div className={styles.tabContent}>
      {session?.user ? (
        <>
          <p style={{ margin: 0, color: '#b9b5df' }}>{T.createHint[lang]}</p>
          <input
            type="text"
            placeholder={T.roomName[lang]}
            value={roomName}
            onChange={(ev) => setRoomName(ev.target.value)}
            style={{ marginTop: '1rem', padding: '0.6rem', width: '100%', boxSizing: 'border-box' }}
          />
          <button
            style={{ marginTop: '1rem', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 600 }}
            className="lk-button"
            onClick={startMeeting}
          >
            {T.createBtn[lang]}
          </button>
        </>
      ) : (
        <>
          <p style={{ margin: 0, color: '#b9b5df' }}>
{T.gateMsg[lang]}
          </p>
          <button
            style={{ marginTop: '1rem', background: 'var(--lk-success)', color: '#08210f', fontWeight: 600 }}
            className="lk-button"
            onClick={() => signIn('authentik', { callbackUrl: typeof window !== 'undefined' ? window.location.href : '/' })}
          >
            {T.signInToCreate[lang]}
          </button>
        </>
      )}
      {session?.user && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input
            id="use-e2ee"
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
          ></input>
          <label htmlFor="use-e2ee">{lang === 'ru' ? 'Сквозное шифрование (E2EE)' : 'Enable end-to-end encryption'}</label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <label htmlFor="passphrase">Passphrase</label>
            <input
              id="passphrase"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
            />
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function CustomConnectionTab(props: { label: string }) {
  const router = useRouter();

  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const serverUrl = formData.get('serverUrl');
    const token = formData.get('token');
    if (e2ee) {
      router.push(
        `/custom/?liveKitUrl=${serverUrl}&token=${token}#${encodePassphrase(sharedPassphrase)}`,
      );
    } else {
      router.push(`/custom/?liveKitUrl=${serverUrl}&token=${token}`);
    }
  };
  return (
    <form className={styles.tabContent} onSubmit={onSubmit}>
      <p style={{ marginTop: 0 }}>
        Connect LiveKit Meet with a custom server using LiveKit Cloud or LiveKit Server.
      </p>
      <input
        id="serverUrl"
        name="serverUrl"
        type="url"
        placeholder="LiveKit Server URL: wss://*.livekit.cloud"
        required
      />
      <textarea
        id="token"
        name="token"
        placeholder="Token"
        required
        rows={5}
        style={{ padding: '1px 2px', fontSize: 'inherit', lineHeight: 'inherit' }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input
            id="use-e2ee"
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
          ></input>
          <label htmlFor="use-e2ee">Enable end-to-end encryption</label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <label htmlFor="passphrase">Passphrase</label>
            <input
              id="passphrase"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
            />
          </div>
        )}
      </div>

      <hr
        style={{ width: '100%', borderColor: 'rgba(255, 255, 255, 0.15)', marginBlock: '1rem' }}
      />
      <button
        style={{ paddingInline: '1.25rem', width: '100%' }}
        className="lk-button"
        type="submit"
      >
        Connect
      </button>
    </form>
  );
}

export default function Page() {
  const lang: 'ru' | 'en' = useLang();
  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className="header">
          <div className="kosmos-brand" style={{ fontSize: '30px' }}>
            <img src="/kosmos-logo.png" alt="KOSMOS" width={42} height={42} style={{ filter: 'drop-shadow(0 0 9px rgba(139,92,246,0.45))' }} />
            <span>KOSMOS <b>Video Meet</b></span>
          </div>
          <h2 style={{ color: '#b9b5df', fontWeight: 400, fontSize: '15px', maxWidth: '46ch' }}>
{T.slogan[lang]}
          </h2>
        </div>
        <Suspense fallback="Loading">
          <Tabs>
            <DemoMeetingTab label="Demo" />
            <CustomConnectionTab label="Custom" />
          </Tabs>
        </Suspense>
      </main>
      <footer data-lk-theme="default">KOSMOS Video Meet</footer>
    </>
  );
}
